import { cva, type VariantProps } from 'class-variance-authority';
import { Eye, EyeOff } from 'lucide-react';
import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { cn } from '../cn';

// Brief §6 input idle: bg=panel, text=ink-high, placeholder=ink-low, border=1px border, radius=2px, padding=10px 12px, h=44px (touch target).
// Focus: 1px signal border + 1px signal outline offset 0. Error: destructive border + destructive-soft tint.
const inputVariants = cva(
  'block w-full rounded-sm border bg-panel text-[15px] leading-[1.4] text-ink-high placeholder:text-ink-low ' +
    'h-11 px-3 ' +
    'focus-visible:outline-1 focus-visible:outline-primary focus-visible:outline-offset-0 focus-visible:border-primary ' +
    'disabled:bg-muted disabled:text-ink-low disabled:cursor-not-allowed ' +
    'read-only:text-ink-low',
  {
    variants: {
      state: {
        idle: 'border-border',
        error:
          'border-destructive bg-destructive-soft/40 focus-visible:outline-destructive focus-visible:border-destructive',
      },
    },
    defaultVariants: {
      state: 'idle',
    },
  },
);

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, state, type = 'text', ...props }, ref) => {
    return (
      <input
        ref={ref}
        type={type}
        className={cn(inputVariants({ state }), className)}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Visible label for the eye-toggle for screen readers. */
  toggleLabel?: { show: string; hide: string };
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      state,
      toggleLabel = { show: 'Show password', hide: 'Hide password' },
      ...props
    },
    ref,
  ) => {
    const [revealed, setRevealed] = useState(false);
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={revealed ? 'text' : 'password'}
          state={state}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          aria-label={revealed ? toggleLabel.hide : toggleLabel.show}
          aria-pressed={revealed}
          onClick={() => setRevealed((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center h-7 w-7 rounded-sm text-ink-low hover:text-ink-high focus-visible:outline-1 focus-visible:outline-primary focus-visible:outline-offset-0"
          tabIndex={-1}
        >
          {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
